/* theme.js — global behaviors.
   - Reveal-on-scroll
   - Cart drawer + mobile menu open/close, focus trap, scroll lock, scrim
   - AJAX cart updates (/cart/change.js, /cart/add.js) with optimistic UI
   - Quantity steppers
   - Reduced-motion aware
*/
(function () {
  'use strict';

  var prefersReduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  // ──────────────────────────────────────────────────────────────────────
  // Reveal-on-scroll
  // ──────────────────────────────────────────────────────────────────────
  var inDesignMode = window.Shopify && window.Shopify.designMode;
  function initReveals(root) {
    var scope = root || document;
    var reveals = scope.querySelectorAll('.reveal:not(.in)');
    if (!reveals.length) return;
    if (inDesignMode || prefersReduce || !('IntersectionObserver' in window)) {
      reveals.forEach(function (el) { el.classList.add('in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });
    reveals.forEach(function (el) { io.observe(el); });
  }
  initReveals();
  document.addEventListener('shopify:section:load', function (e) { initReveals(e.target); });
  document.addEventListener('shopify:section:select', function (e) {
    e.target.querySelectorAll('.reveal').forEach(function (el) { el.classList.add('in'); });
  });

  // ──────────────────────────────────────────────────────────────────────
  // UGC video - click-to-open fullscreen player
  // ──────────────────────────────────────────────────────────────────────
  function formatVideoTime(value) {
    var seconds = Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
    var mins = Math.floor(seconds / 60);
    var secs = seconds % 60;
    return mins + ':' + String(secs).padStart(2, '0');
  }

  function initUgcLightbox(section) {
    var lightbox = section && section.querySelector('[data-ugc-lightbox]');
    if (!lightbox) return null;
    if (lightbox.dataset.ugcBound === '1') return lightbox;
    lightbox.dataset.ugcBound = '1';

    var player = lightbox.querySelector('[data-ugc-lb-video]');
    var closeBtn = lightbox.querySelector('[data-ugc-close]');
    var playBtn = lightbox.querySelector('[data-ugc-lb-play]');
    var muteBtn = lightbox.querySelector('[data-ugc-lb-mute]');
    var fullBtn = lightbox.querySelector('[data-ugc-lb-fullscreen]');
    var bar = lightbox.querySelector('[data-ugc-lb-bar]');
    var fill = lightbox.querySelector('[data-ugc-lb-fill]');
    var time = lightbox.querySelector('[data-ugc-lb-time]');
    var stage = lightbox.querySelector('.ugc-lightbox__stage');
    var lastFocus = null;

    function setPlaying() {
      var playing = player && !player.paused && !player.ended;
      lightbox.classList.toggle('is-playing', playing);
      if (playBtn) playBtn.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    }

    function setMuted() {
      var muted = !player || player.muted || player.volume === 0;
      lightbox.classList.toggle('is-muted', muted);
      if (muteBtn) muteBtn.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
    }

    function updateProgress() {
      if (!player) return;
      var duration = Number.isFinite(player.duration) ? player.duration : 0;
      var current = Number.isFinite(player.currentTime) ? player.currentTime : 0;
      var pct = duration ? Math.min(100, Math.max(0, (current / duration) * 100)) : 0;
      if (fill) fill.style.width = pct + '%';
      if (bar) bar.setAttribute('aria-valuenow', String(Math.round(pct)));
      if (time) time.textContent = formatVideoTime(current) + ' / ' + formatVideoTime(duration);
    }

    function setStageAspect(width, height) {
      if (!stage || !width || !height) return;
      var ratio = width / height;
      stage.style.setProperty('--ugc-video-aspect', width + ' / ' + height);
      stage.style.setProperty('--ugc-video-width-by-height', (ratio * 92) + 'vh');
    }

    function syncStageAspectFrom(video) {
      if (!video) return;
      setStageAspect(video.videoWidth, video.videoHeight);
    }

    function previewSrc(preview) {
      var source = preview && preview.querySelector('source');
      return (preview && preview.currentSrc) || (source && source.src) || (source && source.getAttribute('src')) || (preview && preview.getAttribute('src')) || '';
    }

    lightbox.openFromPreview = function (preview) {
      if (!player || !preview) return;
      var src = previewSrc(preview);
      if (!src) return;

      document.querySelectorAll('[data-ugc-video]').forEach(function (video) { video.pause(); });
      lastFocus = document.activeElement;

      if (player.src !== src) {
        player.pause();
        player.src = src;
        player.load();
      }
      var poster = preview.getAttribute('poster');
      if (poster) player.setAttribute('poster', poster);
      else player.removeAttribute('poster');
      if (stage) {
        stage.style.removeProperty('--ugc-video-aspect');
        stage.style.removeProperty('--ugc-video-width-by-height');
      }
      syncStageAspectFrom(preview);

      player.currentTime = 0;
      player.muted = false;
      setMuted();
      updateProgress();
      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.classList.add('ugc-lightbox-open');
      if (closeBtn) closeBtn.focus({ preventScroll: true });

      player.play().then(setPlaying).catch(function () {
        player.muted = true;
        setMuted();
        player.play().then(setPlaying).catch(setPlaying);
      });
    };

    function closeLightbox() {
      if (!lightbox.classList.contains('is-open')) return;
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(function () {});
      }
      player.pause();
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('ugc-lightbox-open');
      setPlaying();
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus({ preventScroll: true });
    }

    function togglePlay() {
      if (!player) return;
      if (player.paused || player.ended) player.play().then(setPlaying).catch(setPlaying);
      else { player.pause(); setPlaying(); }
    }

    function seekToEvent(e) {
      if (!player || !bar || !Number.isFinite(player.duration) || !player.duration) return;
      var rect = bar.getBoundingClientRect();
      var x = Math.min(rect.right, Math.max(rect.left, e.clientX));
      player.currentTime = ((x - rect.left) / rect.width) * player.duration;
      updateProgress();
    }

    if (closeBtn) closeBtn.addEventListener('click', closeLightbox);
    lightbox.addEventListener('click', function (e) { if (e.target === lightbox) closeLightbox(); });
    if (player) {
      player.addEventListener('click', togglePlay);
      player.addEventListener('play', setPlaying);
      player.addEventListener('pause', setPlaying);
      player.addEventListener('ended', setPlaying);
      player.addEventListener('loadedmetadata', function () {
        syncStageAspectFrom(player);
        updateProgress();
      });
      player.addEventListener('durationchange', updateProgress);
      player.addEventListener('timeupdate', updateProgress);
      player.addEventListener('volumechange', setMuted);
    }
    if (playBtn) playBtn.addEventListener('click', togglePlay);
    if (muteBtn) {
      muteBtn.addEventListener('click', function () {
        player.muted = !player.muted;
        setMuted();
      });
    }
    if (bar) {
      bar.addEventListener('click', seekToEvent);
      bar.addEventListener('keydown', function (e) {
        if (!player || !Number.isFinite(player.duration) || !player.duration) return;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.preventDefault();
          player.currentTime = Math.min(player.duration, Math.max(0, player.currentTime + (e.key === 'ArrowRight' ? 5 : -5)));
          updateProgress();
        }
      });
    }
    if (fullBtn) {
      fullBtn.addEventListener('click', function () {
        var target = stage || player;
        if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(function () {});
        else if (target && target.requestFullscreen) target.requestFullscreen().catch(function () {});
      });
    }
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeLightbox();
    });

    setPlaying();
    setMuted();
    updateProgress();
    return lightbox;
  }

  function initUgcVideos(root) {
    var scope = root || document;
    var vids = scope.querySelectorAll('[data-ugc-video]');
    if (!vids.length) return;

    vids.forEach(function (preview) {
      preview.autoplay = false;
      preview.muted = true;
      preview.pause();

      if (preview.dataset.ugcBound === '1') return;
      preview.dataset.ugcBound = '1';

      var section = preview.closest('.ugc-section');
      var lightbox = initUgcLightbox(section);
      var card = preview.closest('.ugc-card');
      var btn = card && card.querySelector('[data-ugc-play]');
      var expand = card && card.querySelector('[data-ugc-expand]');

      function stopRailDrag(ev) {
        if (ev) ev.stopPropagation();
      }

      function openVideo(ev) {
        if (ev) {
          ev.preventDefault();
          ev.stopPropagation();
        }

        if (lightbox && lightbox.openFromPreview) {
          lightbox.openFromPreview(preview);
        }
      }

      /*
        Prevent the rail drag listener from stealing play/expand clicks.
        This keeps drag scrolling working everywhere else.
      */
      [btn, expand].forEach(function (el) {
        if (!el) return;

        el.addEventListener('pointerdown', stopRailDrag, true);
        el.addEventListener('mousedown', stopRailDrag, true);
        el.addEventListener('touchstart', stopRailDrag, true);
        el.addEventListener('click', openVideo, true);
      });

      preview.addEventListener('click', openVideo);
    });
  }
  initUgcVideos();
  document.addEventListener('shopify:section:load', function (e) { initUgcVideos(e.target); });

  // ──────────────────────────────────────────────────────────────────────
  // Drawer / menu system
  // ──────────────────────────────────────────────────────────────────────
  var scrim       = document.getElementById('scrim');
  var cartDrawer  = document.getElementById('cartDrawer');
  var mobileMenu  = document.getElementById('mobileMenu');
  var openCartBtn = document.getElementById('openCart');
  var closeCartBtn = document.getElementById('closeCart');
  var openMenuBtn = document.getElementById('openMenu');
  var closeMenuBtn = document.getElementById('closeMenu');
  var continueShoppingBtn = document.getElementById('continueShopping');
  var openCartFromMenu = document.getElementById('openCartFromMenu');
  var mainEl = document.getElementById('main-content');
  var announceEl = document.querySelector('.topbar-stick--announce');
  var headerEl = document.querySelector('.topbar-stick--header');
  var footerEl = document.querySelector('.footer');
  var lastFocused = null;

  // Keep --announce-h in sync with the live announce bar height so the
  // sticky header stacks just below it across breakpoints + when hidden.
  function syncAnnounceHeight() {
    var h = announceEl ? announceEl.getBoundingClientRect().height : 0;
    document.documentElement.style.setProperty('--announce-h', h + 'px');
  }
  syncAnnounceHeight();
  window.addEventListener('resize', syncAnnounceHeight, { passive: true });

  function setBackgroundInert(yes) {
    [mainEl, announceEl, headerEl, footerEl].forEach(function (el) {
      if (!el) return;
      if (yes) { el.setAttribute('inert', ''); el.setAttribute('aria-hidden', 'true'); }
      else { el.removeAttribute('inert'); el.removeAttribute('aria-hidden'); }
    });
  }
  function lockScroll(yes) { document.body.style.overflow = yes ? 'hidden' : ''; }

  function openDialog(el, firstFocusSelector) {
    if (!el) return;
    lastFocused = document.activeElement;
    el.classList.add('in');
    el.setAttribute('aria-hidden', 'false');
    if (scrim) scrim.classList.add('in');
    setBackgroundInert(true);
    lockScroll(true);
    var focusTarget = (firstFocusSelector && el.querySelector(firstFocusSelector)) || el.querySelector(FOCUSABLE) || el;
    requestAnimationFrame(function () { focusTarget.focus({ preventScroll: true }); });
  }
  function closeDialog(el) {
    if (!el) return;
    el.classList.remove('in');
    el.setAttribute('aria-hidden', 'true');
    var anyOpen = (cartDrawer && cartDrawer.classList.contains('in')) || (mobileMenu && mobileMenu.classList.contains('in'));
    if (!anyOpen) {
      if (scrim) scrim.classList.remove('in');
      setBackgroundInert(false);
      lockScroll(false);
    }
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus({ preventScroll: true });
  }

  function openCart()  { openDialog(cartDrawer, '#closeCart'); if (openCartBtn) openCartBtn.setAttribute('aria-expanded', 'true'); }
  function closeCart() { closeDialog(cartDrawer); if (openCartBtn) openCartBtn.setAttribute('aria-expanded', 'false'); }
  function openMenu()  { openDialog(mobileMenu, '#closeMenu'); if (openMenuBtn) openMenuBtn.setAttribute('aria-expanded', 'true'); }
  function closeMenu() { closeDialog(mobileMenu); if (openMenuBtn) openMenuBtn.setAttribute('aria-expanded', 'false'); }

  if (openCartBtn)  openCartBtn.addEventListener('click', openCart);
  if (closeCartBtn) closeCartBtn.addEventListener('click', closeCart);
  if (openMenuBtn)  openMenuBtn.addEventListener('click', openMenu);
  if (closeMenuBtn) closeMenuBtn.addEventListener('click', closeMenu);
  if (continueShoppingBtn) continueShoppingBtn.addEventListener('click', closeCart);
  if (openCartFromMenu) {
    openCartFromMenu.addEventListener('click', function (e) {
      e.preventDefault(); closeMenu(); setTimeout(openCart, 200);
    });
  }
  if (scrim) scrim.addEventListener('click', function () { closeCart(); closeMenu(); });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') { closeCart(); closeMenu(); return; }
    if (e.key !== 'Tab') return;
    var openEl = (cartDrawer && cartDrawer.classList.contains('in')) ? cartDrawer
               : (mobileMenu && mobileMenu.classList.contains('in')) ? mobileMenu : null;
    if (!openEl) return;
    var items = openEl.querySelectorAll(FOCUSABLE);
    if (!items.length) return;
    var first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  // ──────────────────────────────────────────────────────────────────────
  // Cart AJAX
  // ──────────────────────────────────────────────────────────────────────
  var moneyFormat = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active === 'USD')
    ? function (cents) { return '$' + (cents / 100).toFixed(2).replace(/\.00$/, ''); }
    : function (cents) { return '$' + (cents / 100).toFixed(2).replace(/\.00$/, ''); };

  function refreshCartDOM(cart) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) { el.textContent = cart.item_count; });
    document.querySelectorAll('[data-cart-count-em]').forEach(function (el) { el.textContent = '(' + cart.item_count + ')'; });
    document.querySelectorAll('[data-cart-subtotal]').forEach(function (el) {
      el.textContent = moneyFormat(cart.total_price) + (el.classList.contains('cart-checkout__price') ? ' →' : '');
    });
    document.querySelectorAll('[data-cart-bnpl]').forEach(function (el) {
      el.textContent = moneyFormat(Math.round(cart.total_price / 4));
    });
    // Ship progress
    var drawer = cartDrawer;
    if (drawer) {
      var threshold = parseInt(drawer.getAttribute('data-threshold') || '9000', 10);
      var remaining = Math.max(0, threshold - cart.total_price);
      var fill = drawer.querySelector('[data-ship-fill]');
      var rem = drawer.querySelector('[data-ship-remaining]');
      var cur = drawer.querySelector('[data-ship-current]');
      if (fill) fill.style.width = Math.min(100, cart.total_price / threshold * 100) + '%';
      if (rem) rem.textContent = moneyFormat(remaining);
      if (cur) cur.textContent = moneyFormat(cart.total_price);
      drawer.classList.toggle('is-qualified', cart.total_price >= threshold);
      drawer.classList.toggle('is-empty', cart.item_count === 0);
    }
    // Home-page ship-strip
    var strip = document.querySelector('[data-ship-strip]');
    if (strip) {
      var sThreshold = parseInt(strip.getAttribute('data-threshold') || '9000', 10);
      var sRem = strip.querySelector('[data-ship-remaining]');
      var sCur = strip.querySelector('[data-ship-current]');
      var sFill = strip.querySelector('.ship-bar__fill');
      if (sRem) sRem.textContent = moneyFormat(Math.max(0, sThreshold - cart.total_price));
      if (sCur) sCur.textContent = moneyFormat(cart.total_price);
      if (sFill) sFill.style.width = Math.min(100, cart.total_price / sThreshold * 100) + '%';
    }
  }

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  function changeCart(payload) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    }).then(function (r) { return r.json(); });
  }

  function addToCart(variantId, quantity) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: variantId, quantity: quantity || 1 })
    }).then(function (r) { return r.json(); });
  }

  function rebuildDrawerBody() {
    // Re-fetch the cart drawer section and swap the body. Uses Shopify's section rendering API.
    if (!cartDrawer) return Promise.resolve();
    var sectionId = cartDrawer.closest('[id^="shopify-section-"]');
    var id = sectionId ? sectionId.id.replace('shopify-section-', '') : 'cart-drawer';
    return fetch('/?sections=' + id, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data[id];
        if (!html) return;
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newDrawer = doc.querySelector('#cartDrawer');
        if (!newDrawer) return;
        // Preserve open state
        var wasOpen = cartDrawer.classList.contains('in');
        cartDrawer.innerHTML = newDrawer.innerHTML;
        if (wasOpen) cartDrawer.classList.add('in');
        bindCartHandlers();
      });
  }

  function bindCartHandlers() {
    // Qty steppers
    cartDrawer && cartDrawer.querySelectorAll('[data-qty]').forEach(function (group) {
      var input = group.querySelector('[data-qty-input]');
      var minus = group.querySelector('[data-qty-action="decrease"]');
      var plus = group.querySelector('[data-qty-action="increase"]');
      var item = group.closest('[data-cart-item]');
      var line = item ? parseInt(item.getAttribute('data-line'), 10) : null;
      function commit(qty) {
        if (line == null) return;
        changeCart({ line: line, quantity: qty }).then(function (cart) {
          refreshCartDOM(cart);
          rebuildDrawerBody();
        });
      }
      if (minus) minus.addEventListener('click', function () {
        var v = Math.max(0, (parseInt(input.value, 10) || 0) - 1);
        input.value = v; commit(v);
      });
      if (plus) plus.addEventListener('click', function () {
        var v = (parseInt(input.value, 10) || 0) + 1;
        input.value = v; commit(v);
      });
      if (input) input.addEventListener('change', function () { commit(parseInt(input.value, 10) || 0); });
    });

    // Remove buttons
    cartDrawer && cartDrawer.querySelectorAll('[data-cart-remove]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        var item = e.target.closest('[data-cart-item]');
        if (!item) return;
        var line = parseInt(item.getAttribute('data-line'), 10);
        item.style.transition = 'opacity .3s ease, transform .3s ease';
        item.style.opacity = '0';
        item.style.transform = 'translateX(-20px)';
        changeCart({ line: line, quantity: 0 }).then(function (cart) {
          refreshCartDOM(cart);
          rebuildDrawerBody();
        });
      });
    });

    // Cross-sell add buttons
    cartDrawer && cartDrawer.querySelectorAll('[data-cart-add]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var variantId = btn.getAttribute('data-variant-id');
        if (!variantId) return;
        btn.disabled = true;
        btn.textContent = 'Adding…';
        addToCart(variantId, 1).then(function () {
          return fetchCart();
        }).then(function (cart) {
          refreshCartDOM(cart);
          rebuildDrawerBody();
        }).catch(function () {
          btn.disabled = false;
          btn.textContent = 'Add';
        });
      });
    });
  }

  bindCartHandlers();

  // ===== UGC carousel — prev / next + drag-to-scroll =====
  var ugcRail = document.getElementById('ugcRail');
  if (ugcRail) {
    var prev = document.getElementById('ugcPrev');
    var next = document.getElementById('ugcNext');
    function ugcStep() {
      var card = ugcRail.querySelector('.ugc-card');
      var w = card ? card.getBoundingClientRect().width : 320;
      var gap = parseFloat(getComputedStyle(ugcRail).columnGap || getComputedStyle(ugcRail).gap || 20) || 20;
      return w + gap;
    }
    prev && prev.addEventListener('click', function () { ugcRail.scrollBy({ left: -ugcStep(), behavior: 'smooth' }); });
    next && next.addEventListener('click', function () { ugcRail.scrollBy({ left: ugcStep(),  behavior: 'smooth' }); });

    // pointer drag
    var down = false, startX = 0, startScroll = 0;
    ugcRail.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      down = true; startX = e.clientX; startScroll = ugcRail.scrollLeft;
      ugcRail.classList.add('is-dragging');
      ugcRail.setPointerCapture(e.pointerId);
    });
    ugcRail.addEventListener('pointermove', function (e) {
      if (!down) return;
      ugcRail.scrollLeft = startScroll - (e.clientX - startX);
    });
    function endDrag() {
      if (!down) return;
      down = false;
      ugcRail.classList.remove('is-dragging');
    }
    ugcRail.addEventListener('pointerup', endDrag);
    ugcRail.addEventListener('pointercancel', endDrag);
    ugcRail.addEventListener('pointerleave', endDrag);
  }

  // ===== Newsletter modal =====
  var newsModal = document.getElementById('newsModal');
  if (newsModal) {
    var SUPPRESS_KEY = 'ibyx_news_modal_v1';
    function suppressNews(days) {
      try {
        var until = Date.now() + (days || 30) * 86400000;
        localStorage.setItem(SUPPRESS_KEY, String(until));
      } catch (e) {}
    }
    function newsSuppressed() {
      try {
        var raw = localStorage.getItem(SUPPRESS_KEY);
        return raw && Number(raw) > Date.now();
      } catch (e) { return false; }
    }
    function openNews() {
      if (newsSuppressed()) return;
      openDialog && openDialog(newsModal);
    }

    document.querySelectorAll('[data-open-news]').forEach(function (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        openDialog && openDialog(newsModal);
      });
    });
    var closeNews = document.getElementById('closeNews');
    closeNews && closeNews.addEventListener('click', function () {
      closeDialog && closeDialog(newsModal);
      suppressNews(30);
    });

    var autoOpen = newsModal.getAttribute('data-auto-open') === 'true';
    var delay = parseInt(newsModal.getAttribute('data-delay'), 10);
    if (isNaN(delay)) delay = 10;
    if (autoOpen && !newsSuppressed()) {
      setTimeout(openNews, delay * 1000);
    }

    var exitIntent = newsModal.getAttribute('data-exit-intent') === 'true';
    if (exitIntent && !newsSuppressed() && window.matchMedia('(min-width: 880px)').matches) {
      var exitFired = false;
      document.addEventListener('mouseout', function (e) {
        if (exitFired) return;
        if (e.relatedTarget || e.toElement) return;
        if (e.clientY > 0) return;
        exitFired = true;
        openNews();
      });
    }

    // Success-state swap: if Liquid rendered form.posted_successfully?, or
    // form was just submitted client-side, reveal the code panel.
    var bodyEl = document.getElementById('newsModalBody');
    var successEl = document.getElementById('newsModalSuccess');
    function showNewsSuccess() {
      if (!bodyEl || !successEl) return;
      bodyEl.hidden = true;
      successEl.hidden = false;
      suppressNews(365);
      openDialog && openDialog(newsModal);
    }
    if (window.__newsPosted) showNewsSuccess();
  }

  // ===== PDP: sticky scroll bar (desktop slides from top; mobile from bottom) =====
  var pdpBuybox = document.getElementById('buybox');
  var pdpSticky = document.getElementById('stickyProd');
  if (pdpBuybox && pdpSticky) {
    var mobileMQ = window.matchMedia('(max-width: 880px)');
    var stickyScrollHandler = null;
    var stickyIO = null;

    function teardownSticky() {
      if (stickyScrollHandler) {
        window.removeEventListener('scroll', stickyScrollHandler);
        stickyScrollHandler = null;
      }
      if (stickyIO) {
        stickyIO.disconnect();
        stickyIO = null;
      }
      pdpSticky.classList.remove('in');
      pdpSticky.setAttribute('aria-hidden', 'true');
    }

    function setupSticky() {
      teardownSticky();
      if (mobileMQ.matches) {
        stickyScrollHandler = function () {
          if (window.scrollY > 360) {
            pdpSticky.classList.add('in');
            pdpSticky.setAttribute('aria-hidden', 'false');
          } else {
            pdpSticky.classList.remove('in');
            pdpSticky.setAttribute('aria-hidden', 'true');
          }
        };
        window.addEventListener('scroll', stickyScrollHandler, { passive: true });
        stickyScrollHandler();
      } else if ('IntersectionObserver' in window) {
        stickyIO = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (!e.isIntersecting && e.boundingClientRect.top < 0) {
              pdpSticky.classList.add('in');
              pdpSticky.setAttribute('aria-hidden', 'false');
            } else {
              pdpSticky.classList.remove('in');
              pdpSticky.setAttribute('aria-hidden', 'true');
            }
          });
        }, { threshold: 0, rootMargin: '0px 0px -200px 0px' });
        stickyIO.observe(pdpBuybox);
      }
    }
    setupSticky();
    if (mobileMQ.addEventListener) {
      mobileMQ.addEventListener('change', setupSticky);
    } else if (mobileMQ.addListener) {
      mobileMQ.addListener(setupSticky);
    }

  }

  // ===== PDP: variant matching + AJAX add-to-cart =====
  var productForm = document.getElementById('productForm');
  if (productForm) {
    var variantsScript = productForm.querySelector('[data-product-variants-json]');
    var variants = [];
    try { variants = variantsScript ? JSON.parse(variantsScript.textContent) : []; } catch (err) { variants = []; }

    var variantIdInput = productForm.querySelector('[data-variant-id]');
    var optionGroups = Array.prototype.slice.call(productForm.querySelectorAll('.option-group'));
    var priceMain = document.querySelector('[data-product-price]');
    var priceInline = document.querySelector('[data-product-price-inline]');
    var priceSticky = document.querySelector('[data-sticky-price]');
    var atcBtn = productForm.querySelector('[data-add-to-cart]');
    var atcLabel = productForm.querySelector('[data-add-to-cart-label]');
    var stickyAddBtn = document.querySelector('[data-sticky-add]');
    var galleryMainImgEl = document.getElementById('galleryMainImg');

    function moneyZ(cents) {
      return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
    }

    function findMatchingVariant() {
      if (!variants.length) return null;
      var selections = optionGroups.map(function (g) {
        var active = g.querySelector('.option-chip.active, .option-swatch.active');
        return {
          idx: parseInt(g.getAttribute('data-option-index'), 10),
          val: active ? active.getAttribute('data-option-value') : null
        };
      });
      // First, an exact match across constrained options.
      var exact = variants.find(function (v) {
        return selections.every(function (s) {
          if (s.val == null) return true;
          return (v.options && v.options[s.idx]) === s.val;
        });
      });
      if (exact) return exact;
      // Fall back to an in-stock variant that matches what we can, then any match.
      var anyAvail = variants.find(function (v) { return v.available; });
      return anyAvail || variants[0];
    }

    function setUrlVariant(id) {
      if (!id || !window.history || typeof window.history.replaceState !== 'function') return;
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', id);
        window.history.replaceState({}, '', url.toString());
      } catch (err) { /* noop */ }
    }

    function updateForVariant(v) {
      if (!v) return;
      if (variantIdInput) variantIdInput.value = v.id;
      if (priceMain) priceMain.textContent = moneyZ(v.price);
      if (priceSticky) priceSticky.textContent = moneyZ(v.price);

      if (atcBtn) {
        var inLabel = atcBtn.getAttribute('data-instock-label') || 'Add to cart';
        var outLabel = atcBtn.getAttribute('data-soldout-label') || 'Back Soon';
        if (v.available) {
          atcBtn.disabled = false;
          atcBtn.removeAttribute('aria-disabled');
          if (atcLabel) {
            atcLabel.innerHTML = '';
            atcLabel.appendChild(document.createTextNode(inLabel + ' — '));
            var inlineSpan = document.createElement('span');
            inlineSpan.setAttribute('data-product-price-inline', '');
            inlineSpan.textContent = moneyZ(v.price);
            atcLabel.appendChild(inlineSpan);
            priceInline = inlineSpan;
          }
        } else {
          atcBtn.disabled = true;
          atcBtn.setAttribute('aria-disabled', 'true');
          if (atcLabel) atcLabel.textContent = outLabel;
        }
      }

      if (stickyAddBtn) {
        var stickyIn = stickyAddBtn.getAttribute('data-instock-label') || 'Add to cart';
        var stickyOut = stickyAddBtn.getAttribute('data-soldout-label') || 'Back Soon';
        if (v.available) {
          stickyAddBtn.disabled = false;
          stickyAddBtn.removeAttribute('aria-disabled');
          stickyAddBtn.textContent = stickyIn;
        } else {
          stickyAddBtn.disabled = true;
          stickyAddBtn.setAttribute('aria-disabled', 'true');
          stickyAddBtn.textContent = stickyOut;
        }
      }

      if (galleryMainImgEl && v.featured_image && v.featured_image.src) {
        galleryMainImgEl.removeAttribute('srcset');
        galleryMainImgEl.removeAttribute('sizes');
        galleryMainImgEl.src = v.featured_image.src;
      }

      setUrlVariant(v.id);
    }

    optionGroups.forEach(function (group) {
      var picker = group.querySelector('.option-chips, .option-swatches');
      if (!picker) return;
      picker.addEventListener('click', function (e) {
        var btn = e.target.closest('.option-chip, .option-swatch');
        if (!btn || !picker.contains(btn)) return;
        var selector = btn.classList.contains('option-chip') ? '.option-chip' : '.option-swatch';
        picker.querySelectorAll(selector).forEach(function (c) {
          c.classList.remove('active');
          c.setAttribute('aria-pressed', 'false');
          c.setAttribute('aria-checked', 'false');
        });
        btn.classList.add('active');
        btn.setAttribute('aria-pressed', 'true');
        btn.setAttribute('aria-checked', 'true');
        var labelEl = group.querySelector('.option-label__value');
        if (labelEl) {
          var label = btn.textContent.trim() || btn.getAttribute('aria-label') || '';
          labelEl.textContent = label;
        }
        var v = findMatchingVariant();
        updateForVariant(v);
      });
    });

    function submitProduct() {
      if (!variantIdInput || !variantIdInput.value) return;
      if (atcBtn && atcBtn.disabled) return;
      var qtyInput = productForm.querySelector('input[name="quantity"]');
      var qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
      var addons = Array.prototype.slice.call(productForm.querySelectorAll('input[name="dft_addon[]"]:checked'))
        .map(function (i) { return i.value; })
        .filter(Boolean);

      var origText = atcLabel ? atcLabel.innerHTML : '';
      if (atcBtn) atcBtn.disabled = true;
      if (atcLabel) atcLabel.textContent = 'Adding…';

      addToCart(variantIdInput.value, qty)
        .then(function () {
          return addons.reduce(function (p, id) {
            return p.then(function () { return addToCart(id, 1); });
          }, Promise.resolve());
        })
        .then(fetchCart)
        .then(function (cart) {
          refreshCartDOM(cart);
          return rebuildDrawerBody();
        })
        .then(function () {
          openCart();
        })
        .catch(function () {
          // restore label on failure
          if (atcLabel) atcLabel.innerHTML = origText;
        })
        .finally(function () {
          if (atcBtn) atcBtn.disabled = false;
        });
    }

    productForm.addEventListener('submit', function (e) {
      e.preventDefault();
      submitProduct();
    });
    if (stickyAddBtn) {
      stickyAddBtn.addEventListener('click', function () { submitProduct(); });
    }

    // Initial sync (covers ?variant= deep link from selected_or_first_available_variant).
    updateForVariant(findMatchingVariant());
  }

  // ===== PDP: gallery dots + thumbs (aria-selected sync + swap main image) =====
  function syncGallery(group, target) {
    group.querySelectorAll('[data-gallery-index]').forEach(function (el) {
      el.classList.remove('active');
      el.setAttribute('aria-selected', 'false');
    });
    target.classList.add('active');
    target.setAttribute('aria-selected', 'true');
    var src = target.getAttribute('data-media-src');
    var mainImg = document.getElementById('galleryMainImg');
    if (src && mainImg) {
      mainImg.removeAttribute('srcset');
      mainImg.removeAttribute('sizes');
      mainImg.src = src;
    }
    // Also sync the sibling group (dots <-> thumbs)
    var idx = target.getAttribute('data-gallery-index');
    document.querySelectorAll('[data-gallery-index="' + idx + '"]').forEach(function (sibling) {
      if (sibling === target) return;
      var siblingGroup = sibling.closest('.gallery__nav, .gallery__thumbs');
      if (!siblingGroup) return;
      siblingGroup.querySelectorAll('[data-gallery-index]').forEach(function (s) {
        s.classList.remove('active');
        s.setAttribute('aria-selected', 'false');
      });
      sibling.classList.add('active');
      sibling.setAttribute('aria-selected', 'true');
    });
  }
  document.querySelectorAll('.gallery__nav, .gallery__thumbs').forEach(function (group) {
    group.addEventListener('click', function (e) {
      var t = e.target.closest('[data-gallery-index]');
      if (!t || !group.contains(t)) return;
      syncGallery(group, t);
    });
  });

  // ===== PDP: review filter chips (visual only — Judge.me hook reads data-review-filter) =====
  document.querySelectorAll('[data-review-filters]').forEach(function (group) {
    group.addEventListener('click', function (e) {
      var chip = e.target.closest('.review-chip');
      if (!chip || !group.contains(chip)) return;
      group.querySelectorAll('.review-chip').forEach(function (c) { c.classList.remove('active'); });
      chip.classList.add('active');
    });
  });

  // ===== PDP: quantity steppers on the main product form =====
  document.querySelectorAll('.buybox__qty').forEach(function (group) {
    var input = group.querySelector('input[name="quantity"]');
    if (!input) return;
    var down = group.querySelector('[data-qty-down]');
    var up = group.querySelector('[data-qty-up]');
    function clamp(v) {
      v = parseInt(v, 10);
      if (isNaN(v) || v < 1) v = 1;
      if (v > 99) v = 99;
      return v;
    }
    down && down.addEventListener('click', function () { input.value = clamp(parseInt(input.value, 10) - 1); });
    up && up.addEventListener('click', function () { input.value = clamp(parseInt(input.value, 10) + 1); });
  });

  // ===== COLLECTION: category-tile rail nav =====
  document.querySelectorAll('[data-cat-tiles-rail]').forEach(function (rail) {
    var section = rail.closest('.cat-tiles') || document;
    var prev = section.querySelector('[data-cat-tiles-prev]');
    var next = section.querySelector('[data-cat-tiles-next]');
    function step() {
      var card = rail.querySelector('.cat-tile');
      var w = card ? card.getBoundingClientRect().width : 200;
      var gap = parseFloat(getComputedStyle(rail).columnGap || getComputedStyle(rail).gap || 18) || 18;
      return w + gap;
    }
    var behavior = prefersReduce ? 'auto' : 'smooth';
    if (prev) prev.addEventListener('click', function () { rail.scrollBy({ left: -step(), behavior: behavior }); });
    if (next) next.addEventListener('click', function () { rail.scrollBy({ left:  step(), behavior: behavior }); });
  });

  // ===== COLLECTION: sort auto-submits the filter form =====
  document.querySelectorAll('[data-filter-sort]').forEach(function (sel) {
    sel.addEventListener('change', function () {
      var form = sel.closest('form');
      if (form) form.submit();
    });
  });

  // ===== COLLECTION: only one filter dropdown open at a time =====
  document.querySelectorAll('[data-filter-chip]').forEach(function (det) {
    det.addEventListener('toggle', function () {
      if (!det.open) return;
      document.querySelectorAll('[data-filter-chip]').forEach(function (other) {
        if (other !== det) other.open = false;
      });
    });
    // Close on outside click
    document.addEventListener('click', function (e) {
      if (det.open && !det.contains(e.target)) det.open = false;
    });
  });

  // ===== COLLECTION: product-card finish swatches (swap card image) =====
  document.querySelectorAll('[data-card-swatch]').forEach(function (sw) {
    sw.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      var card = sw.closest('.product-card');
      if (!card) return;
      card.querySelectorAll('[data-card-swatch]').forEach(function (s) {
        s.classList.remove('is-active');
        s.setAttribute('aria-checked', 'false');
      });
      sw.classList.add('is-active');
      sw.setAttribute('aria-checked', 'true');
      var img = card.querySelector('.product-card__media .ph__img');
      var newSrc = sw.getAttribute('data-image');
      var newSrcset = sw.getAttribute('data-srcset');
      if (img && newSrc) {
        img.setAttribute('src', newSrc);
        if (newSrcset) img.setAttribute('srcset', newSrcset);
      }
    });
  });

  // ===== COLLECTION: product-card Quick Add (single-variant cards) =====
  document.querySelectorAll('[data-quick-add]').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      var variantId = btn.getAttribute('data-variant-id');
      if (!variantId || btn.disabled) return;
      var label = btn.querySelector('.product-card__quick-add-label');
      var originalText = label ? label.textContent : '';
      btn.disabled = true;
      btn.classList.add('is-added');
      if (label) label.textContent = 'Adding…';
      addToCart(variantId, 1)
        .then(fetchCart)
        .then(function (cart) {
          refreshCartDOM(cart);
          return rebuildDrawerBody();
        })
        .then(function () {
          if (label) label.textContent = 'Added';
          setTimeout(function () {
            btn.classList.remove('is-added');
            btn.disabled = false;
            if (label) label.textContent = originalText || 'Quick Add';
          }, 1200);
          openCart();
        })
        .catch(function () {
          btn.classList.remove('is-added');
          btn.disabled = false;
          if (label) label.textContent = originalText || 'Quick Add';
        });
    });
  });

  // ---- Article: reading progress bar ----
  var readingFill = document.querySelector('[data-reading-fill]');
  var articleEl = document.querySelector('[data-article]');
  if (readingFill && articleEl) {
    var onScroll = function () {
      var rect = articleEl.getBoundingClientRect();
      var total = rect.height - window.innerHeight;
      var scrolled = Math.max(0, -rect.top);
      var pct = total > 0 ? Math.min(100, Math.max(0, (scrolled / total) * 100)) : 0;
      readingFill.style.width = pct + '%';
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // ---- Article: copy-link share button ----
  document.querySelectorAll('[data-copy-link]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var url = btn.getAttribute('data-link') || window.location.href;
      var done = function () {
        var prev = btn.getAttribute('aria-label') || 'Copy link';
        btn.setAttribute('aria-label', 'Link copied');
        btn.classList.add('is-copied');
        setTimeout(function () {
          btn.setAttribute('aria-label', prev);
          btn.classList.remove('is-copied');
        }, 1500);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(done).catch(function () {});
      } else {
        var t = document.createElement('textarea');
        t.value = url; document.body.appendChild(t); t.select();
        try { document.execCommand('copy'); done(); } catch (e) {}
        document.body.removeChild(t);
      }
    });
  });

})();
